import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";

// Critical pages - lazy loaded like other pages for consistent Helmet behavior
const HomePage = lazy(() => import("@/pages/HomePage"));
import AdminLogin from "@/pages/admin/AdminLogin";

// Lazy load all other pages for better performance
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("@/pages/ProjectDetailPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const VideosPage = lazy(() => import("@/pages/VideosPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Service Pages
const FrederickPage = lazy(() => import("@/pages/city/FrederickPage"));
const GermantownMDPage = lazy(() => import("@/pages/city/GermantownMDPage"));
const HagerstownMDPage = lazy(() => import("@/pages/city/HagerstownMDPage"));
const BuffAndRecoatPage = lazy(() => import("@/pages/BuffAndRecoatPage"));
const HardwoodRefinishingPage = lazy(() => import("@/pages/services/HardwoodRefinishingPage"));
const LVPInstallationPage = lazy(() => import("@/pages/services/LVPInstallationPage"));
const StairFlooringPage = lazy(() => import("@/pages/services/StairFlooringPage"));

// Employee Schedule (noindex)
const SchedulePage = lazy(() => import("@/pages/SchedulePage"));

const NextDayFlooringPage = lazy(() => import("@/pages/NextDayFlooringPage"));
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));

// Admin pages - lazy loaded
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProjects = lazy(() => import("@/pages/admin/AdminProjects"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));
const AdminTestimonials = lazy(() => import("@/pages/admin/AdminTestimonials"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminUpload = lazy(() => import("@/pages/admin/AdminUpload"));
const AdminVideos = lazy(() => import("@/pages/admin/AdminVideos"));
const AdminSiteContent = lazy(() => import("@/pages/admin/AdminSiteContent"));
const AdminLandingPage = lazy(() => import("@/pages/admin/AdminLandingPage"));
const AdminAIMeetings = lazy(() => import("@/pages/admin/AdminAIMeetings"));
const AdminMeetingDetail = lazy(() => import("@/pages/admin/AdminMeetingDetail"));
const AdminEstimates = lazy(() => import("@/pages/admin/AdminEstimates"));
const AdminEstimateDetail = lazy(() => import("@/pages/admin/AdminEstimateDetail"));
const AdminSchedules = lazy(() => import("@/pages/admin/AdminSchedules"));

// Context
import { AuthProvider } from "@/context/AuthContext";
import { QuoteModalProvider } from "@/context/QuoteModalContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { LandingPageContentProvider } from "@/context/LandingPageContentContext";

// Fast loading spinner
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FFFAED]">
    <div className="w-8 h-8 border-4 border-[#C6A87C] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SiteContentProvider>
          <LandingPageContentProvider>
            <QuoteModalProvider>
              <BrowserRouter>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/projects/:slug" element={<ProjectDetailPage />} />
                    <Route path="/videos" element={<VideosPage />} />
                    <Route path="/reviews" element={<ReviewsPage />} />
                    
                    {/* Service Pages */}
                    <Route path="/flooring-installation-frederick-md" element={<FrederickPage />} />
                    <Route path="/hardwood-floor-refinishing-frederick-md" element={<HardwoodRefinishingPage />} />
                    <Route path="/lvp-installation-frederick-md" element={<LVPInstallationPage />} />
                    <Route path="/buff-and-recoat-frederick-md" element={<BuffAndRecoatPage />} />
                    <Route path="/stair-flooring-frederick-md" element={<StairFlooringPage />} />
                    
                    {/* Location Pages */}
                    <Route path="/flooring-installation-germantown-md" element={<GermantownMDPage />} />
                    <Route path="/flooring-installation-hagerstown-md" element={<HagerstownMDPage />} />
                    
                    <Route path="/nextday-flooring" element={<NextDayFlooringPage />} />
                    <Route path="/booking" element={<BookingPage />} />
                    <Route path="/booking/:leadId" element={<BookingPage />} />
                    
                    {/* Admin Routes */}
                    <Route path="/admin" element={<AdminLogin />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/site-content" element={<AdminSiteContent />} />
                    <Route path="/admin/landing-page" element={<AdminLandingPage />} />
                    <Route path="/admin/ai-meetings" element={<AdminAIMeetings />} />
                    <Route path="/admin/ai-meetings/:meetingId" element={<AdminMeetingDetail />} />
                    <Route path="/admin/ai-estimates" element={<AdminEstimates />} />
                    <Route path="/admin/ai-estimates/:estimateId" element={<AdminEstimateDetail />} />
                    <Route path="/admin/projects" element={<AdminProjects />} />
                    <Route path="/admin/videos" element={<AdminVideos />} />
                    <Route path="/admin/upload" element={<AdminUpload />} />
                    <Route path="/admin/leads" element={<AdminLeads />} />
                    <Route path="/admin/bookings" element={<AdminBookings />} />
                    <Route path="/admin/testimonials" element={<AdminTestimonials />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />
                    <Route path="/admin/schedules" element={<AdminSchedules />} />
                    
                    {/* Employee Schedule Portal (noindex, separate auth) */}
                    <Route path="/schedule" element={<SchedulePage />} />
                    
                    {/* Client Portal (Public) */}
                    <Route path="/client/:portalCode" element={<ClientPortalPage />} />
                    
                    {/* 404 Catch-all */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            </QuoteModalProvider>
          </LandingPageContentProvider>
        </SiteContentProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
